import { useOne, useParse } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { HistoryLogButton } from "components/historyLogButton";
import { Label } from "components/label";
import { SwitchButton } from "components/switchButton";
import { IAdminAccountRoleDetail, IPermission } from "interfaces";
import { Permissions } from "./components";
import { Link } from "react-router-dom";
import Button from "components/button";
import { PencilIcon } from "@heroicons/react/outline";
import { isHaveActionPermission } from "utills/helpers";

export const AdminAccountRoleDetail: React.FC = () => {

  const parse = useParse();
  const { params } = parse();
  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
  } = useOne<IAdminAccountRoleDetail>({
    resource: "admin-account/role",
    id: params?.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!params?.id,
    } as any,
  });

  const {
    data: permissions,
    isFetching: isFetchingPermissions,
  } = useOne<IPermission[]>({
    resource: "admin-account/permissions",
    id: "maximum",
    meta: {
      resource: "data",
      filters: [
        {
          field: "scope",
          operator: "eq",
          value: "admin",
        },
      ],
    },
  });

  const permissionsData = permissions?.data ?? [];
  
  const roleDetail = data?.data ?? null;

  if (isLoading) {
    return <FullScreenLoading />
  }

  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        customPageLabel="Role Detail"
        additionalLabel={
          <div className="flex gap-x-2.5">
            <div>
              <HistoryLogButton
                resource={`admin-account/role/${roleDetail?.role.id}/history`}
                className="sm:h-auto"
              />
            </div>
            {isHaveActionPermission("role", "admin", "edit") ? (
              <Link to={`/admin-accounts/roles/${roleDetail?.role.id}/edit`}>
                <Button className="h-10 w-10 sm:h-auto sm:w-auto">
                  <span className="sr-only">Edit</span>
                  <PencilIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Edit</span>
                </Button>
              </Link>
            ) : (
              <></>
            )}
          </div>
        }
      />

      <div className="box-container p-0">
        {roleDetail ? (
          <div className="divide-y divide-gray-100">
            <div className="box-container bg-transparent">
              <div className="grid gap-x-4 gap-y-4 sm:grid-cols-6 lg:gap-x-6">
                <div className="sm:col-span-3 flex items-start gap-x-2">
                  <div className="flex-1">
                    <Label required id="is_active" name="Status" />
                    <SwitchButton checked={roleDetail?.role.is_active ? true : false} onChange={() => {}} disabled />
                  </div>
                  <div className="flex-1 text-sm">
                    <Label required id="name" name="Role name" />
                    <p>{roleDetail?.role.name}</p>
                  </div>
                </div>
                <div className="sm:col-span-3 text-sm">
                  <Label id="description" name="Role description" />
                  <p>{roleDetail?.role.description}</p>
                </div>
                <div className="col-span-full">
                  <Label required id="role_permission" name="Access Permission" />
                  <Permissions
                    disabled
                    data={roleDetail?.role_permission ?? []}
                    permissionMaximumData={permissionsData}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : <></>}
      </div>
      {((isFetching && isPreviousData) || isFetchingPermissions) ? <FullScreenLoading /> : <></>}
    </div>
  );
}